import React, { useContext } from 'react';
import { PapiComment } from '@wix/da-papi-types';
import { CommentContext, CommentContextProvider } from '../../CommentContext';
import { CommentContextProviderProps } from '../../CommentContext/CommentContextProvider';
import LoadableCommentReduxModule from '../../redux/LoadableCommentReduxModule';

enum View {
  /** The current user is editing one of their own comments. */
  Editing = 'editing',

  /** The comment has been marked as spam. */
  Spam = 'spam',

  /** The comment is hidden for some reason. Likely moderation */
  Hidden = 'hidden',

  /** Shown when the comment has been orphaned */
  Orphan = 'orphan',

  /** The usual view when nothing is wrong with the comment and the user isn't editing it. */
  Read = 'read',
}

/** Checks a couple flags and spits out the current view we should show for a comment. */
function getCommentView({ comment, isEditing, isPeeking }): View {
  const { isSpam, isHidden, isDeleted, isAdminHidden, replies } = comment;

  const isVisible = !isAdminHidden && !isDeleted;

  if (isEditing) {
    return View.Editing;
  } else if (!isPeeking && isSpam && replies !== 0) {
    return View.Spam;
  } else if (!isPeeking && (isHidden || !isVisible) && replies !== 0) {
    return View.Hidden;
  } else if ((isHidden || !isVisible || isSpam) && !isPeeking) {
    return View.Orphan;
  } else {
    return View.Read;
  }
}

export interface Props extends Pick<
  CommentContextProviderProps,
  'onEditingStateChange' | 'extraActions'
> {
  className?: string;
  comment: PapiComment;

  /** Shown when the current user is editing one of their own comments. */
  editingView: React.ReactNode;

  /** Shown when the comment has been marked as spam. */
  spamView: React.ReactNode;

  /** Shown when the comment is hidden for some reason. Likely moderation. */
  hiddenView: React.ReactNode;

  // TODO: Not 100% on the causes for orphan comments, so if you know, it'd be helpful to add an example or two here.
  /** Shown when the comment has been orphaned. */
  orphanView?: React.ReactNode;

  /** The content of the comment. This is when the user can interact via liking, awarding badges, replying. */
  readView: React.ReactNode;
}

const CommentSkeletonViewSwitcher: React.FC<Omit<Props, 'onEditingStateChange' | 'comment'>> = ({
  editingView,
  spamView,
  hiddenView,
  orphanView,
  readView,
}) => {
  const { comment } = useContext(CommentContext);
  const { isEditing, isPeeking } = useContext(CommentContext);
  const view = getCommentView({ comment, isPeeking, isEditing });
  switch (view) {
    // Fragments here because typescript complains otherwise.
    case View.Editing:
      return <>{editingView}</>;
    case View.Spam:
      return <>{spamView}</>;
    case View.Hidden:
      return <>{hiddenView}</>;
    case View.Orphan:
      return <>{orphanView}</>;
    case View.Read:
      return <>{readView}</>;
    default:
      return null;
  }
};
CommentSkeletonViewSwitcher.displayName = 'CommentSkeletonViewSwitcher';

/** A skeleton for the various views that a comment preset needs to implement. */
const CommentSkeleton: React.FC<Props> = ({
  comment,
  className,
  onEditingStateChange,
  extraActions,
  ...rest
}) => {
  return (
    <CommentContextProvider
      comment={comment}
      onEditingStateChange={onEditingStateChange}
      extraActions={extraActions}
    >
      <LoadableCommentReduxModule />
      <div className={className}>
        <CommentSkeletonViewSwitcher {...rest} />
      </div>
    </CommentContextProvider>
  );
};
CommentSkeleton.displayName = 'CommentSkeleton';

export default CommentSkeleton;
